/**
 * Module dependencies.
 */

import { Svg } from '@untile/react-core/components/svg';
import { media } from '@untile/react-core/styles/media';
import { Trans, useTranslation } from 'react-i18next';
import ipcgLogoSvg from 'src/assets/ipcg-logo.svg';
import promotorsSvg from 'src/assets/promotors.svg';
import { Container } from 'src/components/core/container';
import { Image } from 'src/components/core/image';
import { Link } from 'src/components/core/links/link';
import { Text } from 'src/components/core/text';
import { formatStringToDate } from 'src/core/utils/formatters';
import { textStyles } from 'src/styles/typography';
import styled from 'styled-components';
import { ifProp } from 'styled-tools';

/**
 * Environment constant.
 */

const brpAssociationUrl = process.env.NEXT_PUBLIC_BRP_ASSOCIATION_URL;
const email = process.env.NEXT_PUBLIC_EMAIL;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled(Container).attrs({ as: 'footer' })`
  ${textStyles.caption}

  align-items: end;
  border-top: 1px solid var(--color-grey200);
  color: var(--color-grey600);
  display: flex;
  grid-area: footer;
  justify-content: space-between;
  padding: 16px 0;
`;

/**
 * `LogosWrapper` styled component.
 */

const LogosWrapper = styled.div`
  display: flex;
  gap: 16px;
  max-height: 32px;
`;

/**
 * `LogoBlock` styled component.
 */

const LogoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/**
 * `StyledLink` styled component.
 */

const StyledLink = styled(Link).attrs({ as: 'a' })<{ bold?: boolean }>`
  ${textStyles.caption}

  appearance: none;
  background: none;
  border: none;
  color: var(--color-grey600);

  &:focus,
  &:hover {
    color: var(--color-grey900);
  }

  ${ifProp('bold', 'font-weight: 700;')}
`;

/**
 * `RightContent` styled component.
 */

const RightContent = styled.div`
  text-align: right;
`;

/**
 * `SupportEmail` styled component.
 */

const SupportEmail = styled.span`
  display: block;

  ${media.min.md`
    display: inline-block;
    margin-left:40px;
  `}
`;

/**
 * Email components.
 */

const emailComponents = { Link: <StyledLink bold href={`mailto:${email}`} /> };

/**
 * `Footer` component.
 */

export function PdfFooter() {
  const { t } = useTranslation();

  return (
    <Wrapper>
      <div>
        {`Copyright © ${new Date().getFullYear()} Metamorfose | `}

        <StyledLink href={brpAssociationUrl}>{t('footer.labels.brpAssociation')}</StyledLink>

        <Text block variant={'caption'}>
          {t('common:labels.chart.generatedAt', {
            date: formatStringToDate(new Date().toISOString(), t('common:format.date'))
          })}
        </Text>
      </div>

      <LogoBlock>
        <Text block fontWeight={700} variant={'caption'}>
          {t('footer.labels.promotor')}
        </Text>

        <Svg icon={promotorsSvg} size={'177px'} />
      </LogoBlock>

      <LogoBlock>
        <Text block fontWeight={700} variant={'caption'}>
          {t('footer.labels.partners')}
        </Text>

        <LogosWrapper>
          <Svg icon={ipcgLogoSvg} size={'52px'} />

          <Image
            alt={t('footer.labels.portugueseCompanies')}
            height={32}
            src={'/images/empresas-portuguesas.png'}
            width={120}
          />

          <Image alt={t('footer.labels.cmvm')} height={32} src={'/images/cmvm.png'} width={77} />
        </LogosWrapper>
      </LogoBlock>

      <RightContent>
        <div>
          <Trans
            components={[
              <StyledLink
                bold
                href={process.env.NEXT_PUBLIC_TERMS_AND_CONDITIONS_URL}
                key={'terms-link'}
                target={'_blank'}
              />,
              <StyledLink bold href={process.env.NEXT_PUBLIC_PRIVACIES_URL} key={'privacy-link'} target={'_blank'} />
            ]}
            i18nKey={'footer.labels.legalLink'}
          />
        </div>

        <SupportEmail>
          <Trans components={emailComponents} i18nKey={'footer.labels.supportEmail'} values={{ email }} />
        </SupportEmail>
      </RightContent>
    </Wrapper>
  );
}
